/**
 * NotFound — página 404 genérica para cualquier ruta inexistente.
 *
 * Se monta como catch-all (`path="*"`) en App.tsx, FUERA de `ProtectedRoute`,
 * para que funcione tanto para usuarios autenticados como anónimos (la consigna
 * fue "para cualquier usuario").
 *
 * Decisiones anti-fuga de información (pedido explícito del owner):
 *   - NO reflejamos el path intentado (`location.pathname`) en el DOM. Evita
 *     reflejo de contenido controlado por el atacante (phishing/clickjacking de
 *     texto) y no le confirma nada sobre la URL.
 *   - Mensaje 100% genérico: no revela qué rutas existen, ni si una ruta está
 *     "protegida" vs "inexistente". Las rutas protegidas las gobierna
 *     `ProtectedRoute` (rol no autorizado → redirige a /home; sin token →
 *     /login), así que NUNCA caen acá. Este 404 solo atrapa paths que no
 *     existen para nadie → no hay diferencia observable por rol.
 *   - El botón de regreso se decide según el estado de sesión del PROPIO
 *     usuario (algo que ya conoce): autenticado → su inicio; anónimo → login.
 *     No consulta ni expone datos del backend.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Result, Button } from "antd";
import { useAuth } from "../../context/AuthContext";

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  // Destino del botón "volver": si hay sesión, al inicio que corresponde a su
  // rol (aseador parte en /aseos, el resto en /home); si no, al login. No
  // filtra nada: refleja el estado que el usuario ya tiene.
  const isAseador = String(user?.rol ?? "").toLowerCase() === "aseador";
  const homeTarget = !token ? "/login" : isAseador ? "/aseos" : "/home";
  const buttonLabel = token ? "Ir al inicio" : "Ir al inicio de sesión";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Result
        status="404"
        title="404"
        subTitle="La página que buscas no existe o fue movida."
        extra={
          <Button type="primary" onClick={() => navigate(homeTarget, { replace: true })}>
            {buttonLabel}
          </Button>
        }
      />
    </div>
  );
};

export default NotFound;
